import React, { useMemo, useState, useCallback } from 'react';
import { Formik, FormikActions } from 'formik';
import { ClassicButton } from 'modules/shared-ui/components';
import { DatePickerControl, SelectControl } from 'modules/shared-form';

import { SelectControlOption } from 'modules/shared/types';
import { formatTechDate } from 'modules/shared/utils';
import { Availability } from 'modules/mariage/models';
import { HourSelectControl, QuestionsControl, AppointmentFormQuestion } from './components';

import { AppointmentFormProps, Appointment } from './appointment-form-type';
import { GroupCheckboxesOption } from '.';

import styles from './appointment-form-component.module.css';

interface AppointmentForm {
    typeId: number;
    employeeChoice: boolean;
    employees: SelectControlOption[];
    questions: AppointmentFormQuestion[];
}

const getSlots = (date: Date, availabilities: Availability[]): Availability | undefined => {
    return availabilities.find(a => formatTechDate(a.date) === formatTechDate(date));
};

export const AppointmentForm: React.FC<AppointmentFormProps> = ({
    model,
    appointmentTypes,
    onAvailabilitiesSearch,
    onSuccess,
}: AppointmentFormProps) => {
    const [selectedForm, setSelectedForm] = useState<AppointmentForm | undefined>(undefined);
    const [availabilities, setAvailabilities] = useState<Availability[]>([]);
    const [slots, setSlots] = useState<string[]>([]);
    const typeOptions: SelectControlOption[] = useMemo(
        () =>
            appointmentTypes.map(s => {
                return {
                    key: `${s.id}`,
                    value: s.id,
                    text: s.name,
                };
            }),
        [appointmentTypes],
    );

    const typeForms: AppointmentForm[] = useMemo(
        () =>
            appointmentTypes
                .filter(s => s.form)
                .map(s => {
                    return {
                        typeId: s.id,
                        employeeChoice: s.form.employeeChoice,
                        employees: s.form.employees
                            ? s.form.employees.map(e => {
                                  return {
                                      key: `${e.id}`,
                                      value: e.id,
                                      text: `${e.firstName}${e.lastName !== '' ? ` ${e.lastName}` : ''}`,
                                  };
                              })
                            : [],
                        questions: s.form.questions
                            ? s.form.questions.map(question => {
                                  const { id, controlType, possibleAnswers, ...questionRest } = question;
                                  let listOptions: SelectControlOption[] | undefined;
                                  let multiSelectOptions: GroupCheckboxesOption[] | undefined;

                                  if (controlType === 'list') {
                                      listOptions = possibleAnswers.map((ans, index) => {
                                          return {
                                              key: `list${id}${index}`,
                                              value: ans,
                                              text: ans,
                                          };
                                      });
                                  }

                                  if (controlType === 'multiSelectList') {
                                      multiSelectOptions = possibleAnswers.map((ans, index) => {
                                          return {
                                              key: `multiSelect${id}${index}`,
                                              label: ans,
                                              checked: false,
                                          };
                                      });
                                  }

                                  return {
                                      id,
                                      controlType,
                                      possibleAnswers,
                                      listOptions,
                                      multiSelectOptions,
                                      ...questionRest,
                                  };
                              })
                            : [],
                    };
                }),
        [appointmentTypes],
    );

    const handleSubmit = useCallback(
        (values: Appointment, formikActions: FormikActions<Appointment>) => {
            const allQuestions = selectedForm ? selectedForm.questions : [];
            if (values.answers.length !== allQuestions.length) {
                allQuestions.forEach((question, index) => {
                    const answer = values.answers.find(ans => ans && ans !== null && ans.questionId === question.id);
                    if (!answer) {
                        formikActions.setFieldValue(`answers[${index}].questionId`, []);
                        values.answers.push({
                            questionId: question.id,
                            answer: '',
                        });
                    }
                });
            }
            onSuccess(values);
        },
        [selectedForm, onSuccess],
    );

    const updateAvailabilities = (type: number, date: Date, formActions: FormikActions<Appointment>): void => {
        if (type !== -1) {
            onAvailabilitiesSearch(type, new Date(date.getFullYear(), date.getMonth()))
                .then(res => {
                    const newAvailabilities = res.filter(a => {
                        const sameMonth = new Date(a.date).getMonth() === date.getMonth();
                        return sameMonth;
                    });
                    const newDate = new Date(newAvailabilities[0].date);

                    setAvailabilities(newAvailabilities);

                    const curDateAvailability = getSlots(date, newAvailabilities);
                    const newDateAvailability = getSlots(newDate, newAvailabilities);

                    const keepCurDate = curDateAvailability !== undefined;

                    // set le tableau d'horaires
                    const finalAvailability = keepCurDate ? curDateAvailability : newDateAvailability;
                    setSlots(finalAvailability && finalAvailability.slots ? finalAvailability.slots : []);

                    // set le champ date du formulaire
                    formActions.setFieldValue('date', keepCurDate ? date : newDate);
                })
                .catch(() => {
                    setAvailabilities([]);
                    setSlots([]);
                    formActions.setFieldValue('date', date);
                });
        }
    };

    const updateTimeSlots = (date: Date): void => {
        const availability = getSlots(date, availabilities);
        setSlots(availability && availability.slots ? availability.slots : []);
    };

    return (
        <React.Fragment>
            <Formik<Appointment>
                initialValues={model}
                onSubmit={handleSubmit}
                render={props => (
                    <form>
                        <div className={styles.formContainer}>
                            <div className={styles.fieldsContainer}>
                                <div className={styles.firstColumn}>
                                    <div className={styles.label}>Date de votre mariage*</div>
                                    <div className={styles.datePicker}>
                                        <DatePickerControl name="weddingDate" required />
                                    </div>

                                    <div className={styles.label}>Type de rendez-vous*</div>
                                    <SelectControl
                                        name="typeId"
                                        options={typeOptions}
                                        required
                                        placeholder="Type de rendez-vous"
                                        onChange={type => {
                                            if (typeof type === 'number') {
                                                const typeform = typeForms.find(t => t.typeId === type);
                                                setSelectedForm(typeform);
                                                const { date } = props.values;
                                                updateAvailabilities(type, date, props);
                                            }
                                        }}
                                    />

                                    {props.values.typeId !== -1 && (
                                        <>
                                            <div className={styles.label}>Date de rendez-vous*</div>
                                            <div className={styles.datePicker}>
                                                <DatePickerControl
                                                    altStyle
                                                    name="date"
                                                    required
                                                    disableKeyPress
                                                    minDate={new Date()}
                                                    onDateChange={date => {
                                                        if (date instanceof Date) {
                                                            updateTimeSlots(date);
                                                        }
                                                    }}
                                                    onMonthChange={date => {
                                                        if (date instanceof Date) {
                                                            const type = props.values.typeId;
                                                            updateAvailabilities(type, date, props);
                                                        }
                                                    }}
                                                    onYearChange={date => {
                                                        if (date instanceof Date) {
                                                            const type = props.values.typeId;
                                                            updateAvailabilities(type, date, props);
                                                        }
                                                    }}
                                                    filterDate={date => {
                                                        const hasFreeSlots =
                                                            getSlots(date, availabilities) !== undefined;
                                                        return hasFreeSlots;
                                                    }}
                                                />
                                            </div>
                                            {availabilities && (
                                                <React.Fragment>
                                                    <div className={styles.label}>Créneaux disponibles : </div>
                                                    <HourSelectControl name="slot" slots={slots} required />
                                                </React.Fragment>
                                            )}
                                        </>
                                    )}

                                    {selectedForm && selectedForm.employeeChoice && (
                                        <div>
                                            <div className={styles.label}>
                                                Vous pouvez choisir la personne qui s&apos;occupera de vous
                                            </div>
                                            <SelectControl
                                                name="employeeId"
                                                options={selectedForm.employees}
                                                customNoSelectionOption={{
                                                    key: '',
                                                    value: -1,
                                                    text: '--',
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>

                                {selectedForm && selectedForm.questions && (
                                    <div className={styles.secondColumn}>
                                        <QuestionsControl
                                            name="answers"
                                            questions={selectedForm.questions}
                                            formProps={props}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className={styles.buttonsContainer}>
                                <ClassicButton
                                    text="SUIVANT"
                                    onClick={() => props.submitForm()}
                                    disabled={!props.isValid}
                                />
                            </div>
                        </div>
                    </form>
                )}
            />
        </React.Fragment>
    );
};
